import { Klydo, SoundMode } from '../api/klydo';
import { KLYDO_MANDATORY_FIELDS } from '../consts/mandatoryFields';
import { Theme } from '../types';

const THEME_COLOR_KEYS: (keyof Theme)[] = [
  'backgroundColor',
  'dialsColor',
  'handsColor',
  'pendulumColor',
  'pendulumRodColor',
];

export const isThemeComplete = (
  theme: Partial<Theme> | undefined,
): theme is Theme => THEME_COLOR_KEYS.every((key) => !!theme?.[key]?.trim());

export const isKlydoDataIncomplete = (
  klydo: Partial<Klydo> | undefined,
): boolean => {
  if (!klydo) return true;

  return (
    !klydo.name ||
    !klydo.loopUrl ||
    !isThemeComplete(klydo.theme) ||
    (!!klydo.chimesUrls?.[0] && !klydo.strikesUrls?.[0]) ||
    (!!klydo.strikesUrls?.[0] && !klydo.chimesUrls?.[0])
  );
};

export const getKlydoMissingFields = (
  klydo: Partial<Klydo> | undefined,
): string[] => {
  if (!klydo) {
    return [
      KLYDO_MANDATORY_FIELDS.name,
      KLYDO_MANDATORY_FIELDS.loopUrl,
      KLYDO_MANDATORY_FIELDS.theme.handsColor,
    ];
  }

  const missingFields: string[] = [];

  if (!klydo.name) {
    missingFields.push(KLYDO_MANDATORY_FIELDS.name);
  }

  if (!klydo.loopUrl) {
    missingFields.push(KLYDO_MANDATORY_FIELDS.loopUrl);
  }

  if (!isThemeComplete(klydo.theme)) {
    missingFields.push(KLYDO_MANDATORY_FIELDS.theme.handsColor);
  }

  if (klydo.chimesUrls?.[0] && !klydo.strikesUrls?.[0]) {
    missingFields.push(KLYDO_MANDATORY_FIELDS.strikesUrls);
  }

  if (klydo.strikesUrls?.[0] && !klydo.chimesUrls?.[0]) {
    missingFields.push(KLYDO_MANDATORY_FIELDS.chimesUrls);
  }

  return missingFields;
};

export const getKlydoMissingFieldsMessage = (
  klydo: Partial<Klydo> | undefined,
  prefix = 'Missing: ',
): string => {
  const missingFields = getKlydoMissingFields(klydo);
  return missingFields.length ? `${prefix}${missingFields.join(', ')}` : '';
};

/**
 * The properties the public feed cannot play. Named so the artist reads the
 * same words the studio uses for them, not field names.
 */
export enum PublicUnsupportedProperty {
  Chime = 'Chime',
  Strike = 'Strike Tone',
  Audio = 'Klydo audio',
}

export type PublicSoundProperties = {
  hasChime: boolean;
  hasStrike: boolean;
  soundMode: SoundMode | undefined;
};

/**
 * Which of the unplayable properties a klydo carries.
 *
 * The feed plays no chime, no strike and no loop audio, so going public means
 * losing them: the api clears these three fields when it approves the
 * publication request (`PublicKlydoSoundStripper`). This list is what the
 * confirmation dialog shows before the artist asks for that.
 *
 * Takes the three values rather than a `Klydo` because the two callers hold
 * them differently — the gallery card reads server state, while the detail view
 * must answer for the artist's UNSAVED edits, which is what the publication
 * request flushes and sends.
 *
 * The alarm is deliberately absent: it is not one of the properties the feed
 * drops.
 */
export const getPublicUnsupportedProperties = ({
  hasChime,
  hasStrike,
  soundMode,
}: PublicSoundProperties): PublicUnsupportedProperty[] => {
  const properties: PublicUnsupportedProperty[] = [];

  if (hasChime) properties.push(PublicUnsupportedProperty.Chime);
  if (hasStrike) properties.push(PublicUnsupportedProperty.Strike);
  // Any mode other than OFF plays the loop's own audio.
  if (soundMode && soundMode !== SoundMode.OFF) {
    properties.push(PublicUnsupportedProperty.Audio);
  }

  return properties;
};
